import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export const membershipsService = {
  async create(payload: { tierId: string; billingCycle?: 'MONTHLY' | 'YEARLY'; paymentMethod?: 'STRIPE' | 'PAYPAL'; couponCode?: string }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      membership: any;
      payment: {
        clientSecret?: string;
        approvalUrl?: string;
        paymentId?: string;
        processorType?: 'STRIPE' | 'PAYPAL';
      };
      coupon?: { code: string; percentOff?: number; amountOff?: number };
    };
  },

  async complete(id: string, payload: { paymentId: string; processorType?: 'STRIPE' | 'PAYPAL' }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${id}/complete`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      membership: any;
    };
  },

  async listMine(params: { status?: string; limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/memberships/me`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { memberships: any[]; pagination: any };
  },

  async cancel(id: string, immediate = true) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${id}/cancel`, { immediate }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async update(id: string, payload: { tierId?: string; billingCycle?: 'MONTHLY' | 'YEARLY' }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/memberships/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async changeTier(membershipId: string, newTierId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/change-tier`, { newTierId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      membership: any;
      proration: { daysRemaining: number; fraction: number; credit: number; amountDue: number };
      payment?: any;
    };
  },

  async pause(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${id}/pause`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      membership: any;
    };
  },

  async resume(id: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${id}/resume`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      message: string;
      membership: any;
    };
  },

  // Scheduled Tier Changes
  async scheduleTierChange(membershipId: string, payload: { newTierId: string; scheduledFor: string; reason?: string }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/scheduled-tier-changes`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data?.message,
      scheduledChange: res.data?.change,
    } as {
      message: string;
      scheduledChange: any;
    };
  },

  async listScheduledTierChanges(membershipId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/memberships/${membershipId}/scheduled-tier-changes`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      scheduledChanges: res.data?.changes || [],
    } as {
      scheduledChanges: any[];
    };
  },

  async cancelScheduledTierChange(membershipId: string, changeId: string, reason?: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/scheduled-tier-changes/${changeId}/cancel`, 
      { reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return {
      message: res.data?.message,
      scheduledChange: res.data?.change,
    } as {
      message: string;
      scheduledChange: any;
    };
  },

  async executeScheduledTierChange(membershipId: string, changeId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/memberships/${membershipId}/scheduled-tier-changes/${changeId}/execute`, 
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data as {
      message: string;
      membership: any;
      payment?: any;
    };
  },
};

